/**
 * OPUS-913 — Button + IconButton, ported from the Claude Design handoff
 * (app/components.jsx). Primary create action = green outline + circle-plus.
 * Coral is brand/destructive only.
 */
import React, { useState } from "react";
import { Icon } from "./Icon";

export function Button({
  variant = "secondary", size = "md", icon, iconRight, children, onClick,
  disabled, type = "button", title, style = {},
}) {
  const [hover, setHover] = useState(false);
  const sizes = {
    sm: { padding: "6px 11px", fontSize: 12.5, gap: 6, h: 30 },
    md: { padding: "8px 14px", fontSize: 13.5, gap: 7, h: 38 },
    lg: { padding: "11px 18px", fontSize: 14.5, gap: 8, h: 44 },
  }[size];
  const variants = {
    primary: { bg: hover ? "rgba(69,255,167,0.10)" : "transparent", color: "var(--green)", border: "1px solid var(--green)" },
    secondary: { bg: hover ? "var(--surface-3)" : "var(--surface-2)", color: "var(--text)", border: "1px solid var(--border)" },
    ghost: { bg: hover ? "var(--surface-2)" : "transparent", color: "var(--text-muted)", border: "1px solid transparent" },
    danger: { bg: hover ? "rgba(255,69,92,0.16)" : "rgba(255,69,92,0.10)", color: "var(--coral)", border: "1px solid rgba(255,69,92,0.28)" },
    success: { bg: hover ? "rgba(69,255,167,0.16)" : "rgba(69,255,167,0.10)", color: "var(--green)", border: "1px solid rgba(69,255,167,0.28)" },
    coral: { bg: hover ? "var(--coral-hover)" : "var(--coral)", color: "#fff", border: "1px solid transparent" },
  }[variant];
  return (
    <button
      type={type} onClick={onClick} disabled={disabled} title={title}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: sizes.gap,
        padding: sizes.padding, minHeight: sizes.h, fontSize: sizes.fontSize, fontWeight: 600,
        fontFamily: "var(--font-sans)", borderRadius: "var(--r-md)", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1, transition: "background .14s, border-color .14s, transform .05s",
        whiteSpace: "nowrap", background: variants.bg, color: variants.color, border: variants.border, ...style,
      }}
    >
      {icon && variant === "primary" ? (
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: size === "sm" ? 16 : 18, height: size === "sm" ? 16 : 18, borderRadius: "50%", background: "var(--green)", color: "#08140d", flexShrink: 0 }}>
          <Icon name={icon} size={size === "sm" ? 11 : 13} strokeWidth={2.4} />
        </span>
      ) : icon ? <Icon name={icon} size={size === "sm" ? 14 : 16} /> : null}
      {children}
      {iconRight && <Icon name={iconRight} size={size === "sm" ? 14 : 16} />}
    </button>
  );
}

export function IconButton({ icon, onClick, title, size = 16, danger, active, disabled, style = {} }) {
  const [hover, setHover] = useState(false);
  // OPUS-1045: render a styled tooltip from `title` (token-based, keeps the
  // primitive MUI-free) on hover/focus, instead of relying on the bare native
  // browser tooltip. aria-label is preserved for accessibility. Every opus
  // IconButton that passes a `title` now gets a consistent tooltip.
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button" onClick={onClick} disabled={disabled} aria-label={title}
        onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)} onBlur={() => setHover(false)}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 32, height: 32, borderRadius: "var(--r-sm)", border: "1px solid transparent",
          background: hover ? "var(--surface-3)" : "transparent",
          color: active ? "var(--coral)" : danger && hover ? "var(--coral)" : "var(--text-dim)",
          cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1, transition: "all .13s", ...style,
        }}
      >
        <Icon name={icon} size={size} />
      </button>
      {title && hover && !disabled && (
        <span
          role="tooltip"
          style={{
            position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)",
            whiteSpace: "nowrap", background: "var(--surface-3)", color: "var(--text)",
            border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: "4px 8px",
            fontSize: 12, lineHeight: 1.2, fontFamily: "var(--font-sans)", fontWeight: 500,
            boxShadow: "0 4px 14px rgba(0,0,0,0.45)", zIndex: 1200, pointerEvents: "none",
          }}
        >
          {title}
        </span>
      )}
    </span>
  );
}

export default Button;
